/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useState, useRef, useEffect, type JSX } from 'react';
import {
  useAtomValue as useStateSegmentValue,
  useSetAtom as useSetStateSegment,
} from 'jotai';
import {
  RiDownload2Line,
  RiExpandDiagonalLine,
  RiFileForbidLine,
  RiGlobalLine,
  RiMapPinLine,
} from '@remixicon/react';
import styles from './ImagesTab.styles.module.css';
import ImageContentModal from '../ImageContentModal';
import {
  knowledgeCenterStateSegment,
  Image,
} from '../../../../store/markettingContentGeneration/stateSegments/brandsInformationStateSegment';
import {
  fetchImages,
  getImages,
} from '../../../../store/markettingContentGeneration/actions/majorProcessActions/fetchImages';
import useInfiniteScroll from '../../../../hooks/infiniteScroll/useInfiniteScroll';
import Loading from '../../Loading';
import useBrandMessageState from '../../../../hooks/brandInfoPage/useBrandMessageState';
import Button from '../../../atoms/Button';
import Retry from '../Retry';
import InfoLogo from '../InfoLogo';
import BrandsMessage from '../BrandsMessage';
import { useSnackBar } from '../../../../hooks/snackBar/useSnackBar';
import SnackBar, {
  SnackBarPosition,
  SnackBarVariant,
} from '../../../atoms/SnackBar';
import { translate } from '../../../../localization';
import {
  GLOBAL,
  UNITED_STATES,
  LOCAL,
  ARCHETYPE,
} from '../../../../constants/common';
import SystemTooltip, {
  SystemTooltipVariant,
} from '../../../atoms/SystemTooltip';
import USFlagIcon from '../../../atoms/USFlagIcon';
import CountriesPinIcon from '../../../atoms/CountriesPinIcon';
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags';

type CategoryDetails = {
  icon: JSX.Element;
  tooltipText: string;
};

const getCategoryIconWithTooltip = (
  category?: string
): CategoryDetails | null => {
  if (!category) return null;

  const categoryMapping: Record<string, CategoryDetails> = {
    [GLOBAL]: {
      icon: <RiGlobalLine size={16} color='#18181B' />,
      tooltipText: translate('global_source'),
    },
    [UNITED_STATES]: {
      icon: <USFlagIcon />,
      tooltipText: translate('us_source'),
    },
    [LOCAL]: {
      icon: <RiMapPinLine size={16} color='#18181B' />,
      tooltipText: translate('local_source'),
    },
    [ARCHETYPE]: {
      icon: <CountriesPinIcon />,
      tooltipText: translate('archetype_source'),
    },
  };

  return categoryMapping[category] || null;
};

interface ImagesTabProps {
  isNonOnboardedBrand?: boolean;
}

const ImageGrid: React.FC<ImagesTabProps> = ({
  isNonOnboardedBrand = false,
}) => {
  const { images, apiPayloadUsed } = useStateSegmentValue(
    knowledgeCenterStateSegment
  );
  const setBrandInformationSegment = useSetStateSegment(
    knowledgeCenterStateSegment
  );

  // Get feature flags
  const { isAemAssetIntegrationEnabled } = useFeatureFlags();

  const {
    data: imagesData,
    isLoading: isPageLevelLoading,
    errorMessage: pageLevelErrorMessage,
  } = images;

  const loadingRef = useRef<HTMLDivElement>(null!);
  const [showModal, setShowModal] = useState(false);
  const [paginationLevelErrorCount, setPaginationLevelErrorCount] = useState(0);
  const [tabLevelErrorCount, setTabLevelErrorCount] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);

  const {
    showSnackBar,
    isSnackBarVisible,
    hideSnackBar,
    notificationText,
    snackBarTheme,
  } = useSnackBar();

  const fetchData = async (): Promise<Image[]> => {
    // Check pagination based on AEM asset integration status
    if (isAemAssetIntegrationEnabled) {
      // For AEM asset integration, check if nextPageNumber is 0
      if (
        !images?.data?.imagesPagination?.nextPageNumber ||
        images?.data?.imagesPagination?.nextPageNumber === 0
      ) {
        return [];
      }
    } else {
      // For non-AEM asset integration, check nextPageToken
      if (!images?.data?.imagesPagination?.nextPageToken) {
        return [];
      }
    }

    const payload = {
      ...apiPayloadUsed,
      nextPageToken: images?.data?.imagesPagination?.nextPageToken || undefined,
      page: images?.data?.imagesPagination?.nextPageNumber || undefined,
      pageSize: 20,
      aemAssetIntegrationEnabled: isAemAssetIntegrationEnabled,
    };

    const { success, data: imagesResponseData } = await getImages({
      ...payload,
    });

    if (!success) {
      throw new Error('Manually thrown error!');
    }

    // update pagination
    setBrandInformationSegment(prev => {
      return {
        ...prev,
        images: {
          ...prev?.images,
          data: {
            ...prev?.images?.data,
            imagesPagination: {
              ...prev?.images?.data?.imagesPagination,
              ...imagesResponseData?.pagination,
            },
          },
        },
      };
    });

    return imagesResponseData?.images ?? [];
  };

  const {
    hasError: hasPaginationError,
    hasMore,
    resetHasMore: resetPaginationHasMore,
    resetError: resetPaginationError,
    resetConfigs: resetPaginationConfigs,
    setHasMoreFalse: setHasMorePaginationFalse,
  } = useInfiniteScroll<Image>({
    fetchData,
    loadingElementRef: loadingRef,
    initialPage: 2,
    onSuccess: updatedImagesData => {
      setBrandInformationSegment(prev => {
        return {
          ...prev,
          images: {
            ...prev?.images,
            data: {
              ...prev?.images?.data,
              images: [...prev.images.data.images, ...updatedImagesData],
            },
          },
        };
      });

      // reset the pagination level error count to 0 on successfull pagination  retry
      if (paginationLevelErrorCount > 0) {
        setPaginationLevelErrorCount(0);
      }
    },
    onError: () => {
      // increment the count of the pagination error count
      setPaginationLevelErrorCount(prev => prev + 1);
    },
  });

  const handlePaginationLevelRetry = () => {
    // reset the has more and error it will automatically fetch the next data because loader will be visible again
    resetPaginationHasMore();
    resetPaginationError();
  };

  const handleTabLevelRetry = async () => {
    const success = await fetchImages({
      ...apiPayloadUsed,
      page: 1,
      pageSize: 20,
      aemAssetIntegrationEnabled: isAemAssetIntegrationEnabled,
    });
    if (success) {
      // if retry was success than reset the tab level error count
      setTabLevelErrorCount(0);
    } else {
      // increment the tab level error count on failure
      setTabLevelErrorCount(prev => prev + 1);
    }
  };

  const onImageDownload = async (
    imageUrl: string,
    fileName: string = 'downloaded-image'
  ) => {
    try {
      // Attempt to fetch image as blob (requires S3 CORS configuration)
      const response = await fetch(imageUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);

      showSnackBar('Image downloaded successfully.', SnackBarVariant.SUCCESS);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading the image:', error);

      // Check if it's a CORS error
      const isCorsError =
        error instanceof TypeError && error.message.includes('Failed to fetch');

      if (isCorsError) {
        showSnackBar(
          'Image download unavailable. Please contact support if issue persists.',
          SnackBarVariant.ERROR
        );
      } else {
        showSnackBar(
          'Image downloading failed. Please try again after sometime.',
          SnackBarVariant.ERROR
        );
      }
    }
  };

  const {
    state: brandMessageConfig,
    updateScenarioNoData,
    updateScenarioTabLevelError,
    updateScenarioTabLevelApiDown,
    updateScenarioPaginationLevelError,
    updateScenarioPaginationLevelApiDown,
  } = useBrandMessageState();

  useEffect(() => {
    if (
      !isPageLevelLoading &&
      (pageLevelErrorMessage?.length === 0 ||
        !imagesData?.images ||
        imagesData?.images === null ||
        imagesData?.images?.length === 0)
    ) {
      updateScenarioNoData({
        logo: <RiFileForbidLine size={64} color='#A1A1AA' />,
        title: translate('images_no_data_title'),
        message: translate('images_no_data_message'),
      });
      // if there is no data inside the images than this useEffect will handle it
    }
  }, [isPageLevelLoading, imagesData?.images, pageLevelErrorMessage?.length]);

  useEffect(() => {
    updateScenarioTabLevelError(
      <InfoLogo />,
      <Button
        type='button'
        size='m'
        variant='outline'
        colorScheme='#0000C9'
        onClick={handleTabLevelRetry}
      >
        <Retry />
      </Button>
    );
    // if there is any page level error that will be handled by this useEffect
  }, [pageLevelErrorMessage?.length > 0]);

  useEffect(() => {
    // check the pagination level error count greater than 3
    if (paginationLevelErrorCount > 3) {
      // set the message for system down
      updateScenarioPaginationLevelApiDown();
    } else {
      // gives user to retry
      updateScenarioPaginationLevelError(
        null,
        <Button
          type='button'
          size='m'
          variant='outline'
          colorScheme='#0000C9'
          onClick={handlePaginationLevelRetry}
        >
          <Retry />
        </Button>
      );
    }
  }, [paginationLevelErrorCount]);

  useEffect(() => {
    if (
      !isPageLevelLoading &&
      pageLevelErrorMessage?.length > 0 &&
      tabLevelErrorCount > 3
    ) {
      updateScenarioTabLevelApiDown(<InfoLogo />);
      // if tabLevelErrorCount is greater than 3 than we need to update the brand Message config
    }
  }, [tabLevelErrorCount]);

  useEffect(() => {
    if (isPageLevelLoading) {
      // if the pagelevel loading means there is a new request in than case we need to reset  pagination conFig
      resetPaginationConfigs();
    }
  }, [isPageLevelLoading]);

  useEffect(() => {
    if (!isPageLevelLoading) {
      if (isAemAssetIntegrationEnabled) {
        // For AEM asset integration, check nextPageNumber
        if (
          !imagesData?.imagesPagination?.nextPageNumber ||
          imagesData?.imagesPagination?.nextPageNumber === 0
        ) {
          setHasMorePaginationFalse();
        }
      } else {
        // For non-AEM asset integration, check nextPageToken
        if (!imagesData?.imagesPagination?.nextPageToken) {
          setHasMorePaginationFalse();
        }
      }
    }
  }, [
    imagesData,
    isPageLevelLoading,
    isAemAssetIntegrationEnabled,
    setHasMorePaginationFalse,
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowModal(false);
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal]);

  // Early return for non-onboarded brands
  if (isNonOnboardedBrand) {
    return (
      <div className={styles['image-details__loading']}>
        <BrandsMessage
          logo={<RiFileForbidLine size={64} color='#A1A1AA' />}
          title={translate('images_no_data_title')}
          message={translate('images_no_data_message')}
          cta={null}
        />
      </div>
    );
  }

  if (isPageLevelLoading) {
    return (
      <div className={styles['image-details__loading']}>
        <Loading />
      </div>
    );
  }

  if (
    !isPageLevelLoading &&
    (imagesData?.images?.length === 0 || pageLevelErrorMessage?.length > 0)
  ) {
    const { cta, logo, message, title } = brandMessageConfig || {};

    return (
      <div className={styles['image-details__loading']}>
        <BrandsMessage logo={logo} title={title} message={message} cta={cta} />
      </div>
    );
  }

  return (
    <>
      <div className={styles.grid}>
        {imagesData?.images?.map((e: Image, index: number) => (
          <div key={e.name} className={styles.container}>
            <div
              className={styles.tile}
              onClick={() => {
                setShowModal(true);
                setActiveIndex(index);
              }}
              role='button'
              tabIndex={0}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setShowModal(true);
                  setActiveIndex(index);
                }
              }}
            >
              <div className={styles.imagePlaceholder}>
                <span className={styles.icon}>
                  <img src={e.url} alt='details tab' />
                </span>
              </div>

              <div className={styles.overlay}>
                {/* <button
                className={styles.iconButton}
                onClick={() => {
                  setShowModal(true);
                  setActiveIndex(index);
                }}
              >
                <RiExpandDiagonalLine />
              </button> */}

                <button
                  className={styles.iconButton}
                  onClick={() => onImageDownload(e.url, e.name)}
                >
                  <RiDownload2Line />
                </button>
              </div>
            </div>
            <div className={styles.imageContainer__footer}>
              {isAemAssetIntegrationEnabled &&
                e.category &&
                getCategoryIconWithTooltip(e.category) && (
                  <div className={styles.categoryIcon}>
                    <SystemTooltip
                      content={
                        getCategoryIconWithTooltip(e.category)!.tooltipText
                      }
                      variant={SystemTooltipVariant.TOP_LEFT}
                      offset={2}
                    >
                      {getCategoryIconWithTooltip(e.category)!.icon}
                    </SystemTooltip>
                  </div>
                )}
            </div>
          </div>
        ))}
      </div>

      {hasMore && !hasPaginationError && (
        <div
          ref={loadingRef}
          className={styles['claim-details__pagination-loading']}
        >
          <Loading />
        </div>
      )}

      {showModal && (
        <ImageContentModal
          imageSrc={imagesData?.images?.[activeIndex]?.url || ''}
          imageAlt={imagesData?.images?.[activeIndex]?.name || ''}
          current={activeIndex + 1}
          total={imagesData?.images?.length || 0}
          content=''
          handlePageChange={setActiveIndex}
          onClose={() => setShowModal(false)}
          imagesData={{}}
          handleImageDownload={() => {}}
          onImageDownload={onImageDownload}
        />
      )}

      {isSnackBarVisible && (
        <SnackBar
          theme={snackBarTheme}
          notificationText={notificationText}
          hideSnackBar={hideSnackBar}
          position={SnackBarPosition.BOTTOM_CENTER}
        />
      )}
    </>
  );
};

export default ImageGrid;
